import type { DeliveryMode, OrgSettings } from "@kontaktfix/shared";
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardHeader,
  cn,
  Dialog,
  Field,
  Input,
  Select,
  Skeleton,
  StatusDot,
  Switch,
  Tabs,
  Textarea,
} from "@kontaktfix/ui";
import {
  ArrowSquareOutIcon,
  CheckCircleIcon,
  CopyIcon,
  FolderOpenIcon,
  KeyIcon,
  PlusIcon,
  ShieldCheckIcon,
  TrayIcon,
  WarningIcon,
} from "@phosphor-icons/react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { DirectoryPicker } from "@/components/directory-picker";
import { Page, PageHeader } from "@/components/page";
import { api } from "@/lib/api";
import { toastError } from "@/lib/errors";
import { formatDate, formatRelative } from "@/lib/i18n";
import { keys, useInvalidateData, useLists, useMe, useMembers, useSettings } from "@/lib/queries";
import { ApiTab } from "@/pages/api-tab";
import { BillingTab } from "@/pages/billing-tab";
import { IntegrationsTab } from "@/pages/integrations-tab";
import { PartnerTab } from "@/pages/partner-tab";

function useSaveSettings() {
  const client = useQueryClient();
  return async (body: Partial<OrgSettings>) => {
    const saved = await api.patch<OrgSettings>("/api/settings", body);
    client.setQueryData(keys.settings, saved);
    await client.invalidateQueries({ queryKey: keys.me });
    return saved;
  };
}

function GeneralTab({ settings }: { settings: OrgSettings }) {
  const { t } = useTranslation();
  const save = useSaveSettings();
  const [name, setName] = useState(settings.name);
  const [language, setLanguage] = useState(settings.language);
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await save({ name, language });
      toast.success(t("common.saved"));
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card>
      <form onSubmit={submit} className="flex flex-col gap-4 p-5">
        <Field label={t("settings.general.orgName")} htmlFor="s-name">
          <Input id="s-name" value={name} onChange={(e) => setName(e.target.value)} className="max-w-md" />
        </Field>
        <Field
          label={t("settings.general.language")}
          hint={t("settings.general.languageHint")}
          htmlFor="s-lang"
        >
          <Select
            id="s-lang"
            value={language}
            onChange={(e) => setLanguage(e.target.value as "de" | "en")}
            className="max-w-xs"
          >
            <option value="de">Deutsch</option>
            <option value="en">English</option>
          </Select>
        </Field>
        <Field label={t("settings.general.tenant")}>
          <code className="block w-fit rounded-md border border-border bg-subtle px-2.5 py-1.5 font-mono text-[12px] text-fg-muted">
            {settings.tenantId}
          </code>
        </Field>
        <div>
          <Button type="submit" variant="primary" loading={busy}>
            {t("common.save")}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function MembersTab() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const { data: members, isLoading } = useMembers();
  const { data: lists = [] } = useLists();
  const invalidate = useInvalidateData();
  const client = useQueryClient();
  const [picking, setPicking] = useState(false);
  const [removing, setRemoving] = useState<{ id: string; name: string } | null>(null);
  const refresh = () => Promise.all([client.invalidateQueries({ queryKey: keys.members }), invalidate()]);

  const add = async (objectId: string) => {
    try {
      await api.post("/api/members", { objectId, role: "editor" });
      toast.success(t("settings.members.added"));
      setPicking(false);
      await refresh();
    } catch (err) {
      toastError(err);
    }
  };
  const setRole = async (id: string, role: "admin" | "editor") => {
    try {
      await api.patch(`/api/members/${id}`, { role });
      await refresh();
    } catch (err) {
      toastError(err);
    }
  };
  const remove = async () => {
    if (!removing) return;
    try {
      await api.delete(`/api/members/${removing.id}`);
      setRemoving(null);
      await refresh();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Card>
      <CardHeader
        title={t("settings.members.title")}
        description={t("settings.members.text")}
        actions={
          <Button variant="primary" onClick={() => setPicking(true)}>
            <PlusIcon />
            {t("settings.members.add")}
          </Button>
        }
      />
      {isLoading ? (
        <div className="p-5">
          <Skeleton className="h-12" />
        </div>
      ) : (
        <div className="divide-y divide-border">
          {members?.map((m) => {
            const isMe = me?.identity.email === m.email;
            const editsLists = lists.filter((l) => m.listIds.includes(l.id)).map((l) => l.name);
            return (
              <div key={m.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
                <Avatar name={m.name} size={32} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-[13px] font-medium">
                    {m.name}
                    {isMe && <Badge>{t("settings.members.you")}</Badge>}
                    {m.partnerName && <Badge tone="accent">{m.partnerName}</Badge>}
                  </div>
                  <div className="truncate text-[12px] text-fg-muted">
                    {[
                      m.email,
                      m.lastLoginAt
                        ? `${t("settings.members.lastLogin")} ${formatRelative(m.lastLoginAt)}`
                        : t("settings.members.notYet"),
                      m.role === "editor" && editsLists.length
                        ? t("settings.members.editorOf", { lists: editsLists.join(", ") })
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                </div>
                <Select
                  className="w-40"
                  value={m.role}
                  disabled={isMe}
                  onChange={(e) => setRole(m.id, e.target.value as "admin" | "editor")}
                >
                  <option value="admin">{t("roles.admin")}</option>
                  <option value="editor">{t("roles.editor")}</option>
                </Select>
                {!isMe && (
                  <Button size="sm" variant="ghost" onClick={() => setRemoving({ id: m.id, name: m.name })}>
                    {t("common.remove")}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
      <DirectoryPicker
        open={picking}
        onOpenChange={setPicking}
        type="user"
        title={t("settings.members.addTitle")}
        excluded={members?.map((m) => m.objectId) ?? []}
        onPick={(e) => add(e.id)}
      />
      <Dialog
        open={removing !== null}
        onOpenChange={(o) => !o && setRemoving(null)}
        title={t("settings.members.removeTitle", { name: removing?.name })}
        description={t("settings.members.removeText")}
        footer={
          <>
            <Button onClick={() => setRemoving(null)}>{t("common.cancel")}</Button>
            <Button variant="danger" onClick={remove}>
              {t("common.remove")}
            </Button>
          </>
        }
      />
    </Card>
  );
}

function restrictScript(settings: OrgSettings) {
  return `# Kontaktfix: limit access to members of one mail-enabled security group.
# Run in PowerShell as an Exchange administrator (ExchangeOnlineManagement and Microsoft.Graph modules).
Connect-ExchangeOnline
Connect-MgGraph -Scopes "Application.Read.All","AppRoleAssignment.ReadWrite.All"

$GroupName = "Kontaktfix Empfaenger"   # mail-enabled security group with the allowed mailboxes
$AppId = "${settings.connection.clientId ?? "<Kontaktfix application id>"}"

$sp = Get-MgServicePrincipal -Filter "appId eq '$AppId'"
New-ServicePrincipal -AppId $sp.AppId -ObjectId $sp.Id -DisplayName "Kontaktfix"

$group = Get-Group -Identity $GroupName
New-ManagementScope -Name "Kontaktfix scope" -RecipientRestrictionFilter "MemberOfGroup -eq '$($group.DistinguishedName)'"
New-ManagementRoleAssignment -App $sp.Id -Role "Application Contacts.ReadWrite" -CustomResourceScope "Kontaktfix scope"

# Remove the organization-wide Contacts.ReadWrite grant, otherwise the scope has no effect.
$graph = Get-MgServicePrincipal -Filter "appId eq '00000003-0000-0000-c000-000000000000'"
$role = $graph.AppRoles | Where-Object { $_.Value -eq "Contacts.ReadWrite" }
Get-MgServicePrincipalAppRoleAssignment -ServicePrincipalId $sp.Id |
  Where-Object { $_.AppRoleId -eq $role.Id } |
  ForEach-Object { Remove-MgServicePrincipalAppRoleAssignment -ServicePrincipalId $sp.Id -AppRoleAssignmentId $_.Id }

Test-ServicePrincipalAuthorization -Identity $sp.Id
# Tenant: ${settings.tenantId}`;
}

function MethodOption({
  icon,
  title,
  text,
  badge,
  action,
  note,
}: {
  icon: React.ReactNode;
  title: string;
  text: string;
  badge?: string;
  action: React.ReactNode;
  note?: string | null;
}) {
  return (
    <div className="flex flex-1 flex-col gap-3 rounded-lg border border-border p-4">
      <div className="flex gap-3">
        <span className="mt-0.5 text-fg-subtle [&_svg]:size-5">{icon}</span>
        <div>
          <div className="flex flex-wrap items-center gap-2 text-[13.5px] font-medium">
            {title}
            {badge && <Badge tone="accent">{badge}</Badge>}
          </div>
          <p className="mt-0.5 text-[12.5px] text-fg-muted">{text}</p>
        </div>
      </div>
      <div className="mt-auto pl-8">
        {note ? <p className="text-[12.5px] text-warning">{note}</p> : action}
      </div>
    </div>
  );
}

function MicrosoftTab({ settings }: { settings: OrgSettings }) {
  const { t } = useTranslation();
  const search = useSearch({ from: "/app/settings/{-$tab}" });
  const client = useQueryClient();
  const [testing, setTesting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [forcing, setForcing] = useState(false);
  const { status, method, ownApp, disconnecting: cleanup } = settings.connection;
  const connected = status === "granted";

  useEffect(() => {
    if (search.consent === "ok") toast.success(t("settings.microsoft.consentOk"));
    if (search.consent === "error")
      toast.error(t("settings.microsoft.consentError", { reason: search.reason ?? "" }));
  }, [search.consent, search.reason, t]);

  const apply = async (saved: OrgSettings) => {
    client.setQueryData(keys.settings, saved);
    await client.invalidateQueries({ queryKey: keys.me });
  };
  const test = async () => {
    setTesting(true);
    try {
      await apply(await api.post<OrgSettings>("/api/connection/test"));
    } catch (err) {
      toastError(err);
    } finally {
      setTesting(false);
    }
  };
  const disconnect = async (force = false) => {
    try {
      await apply(await api.post<OrgSettings>("/api/connection/disconnect", { force }));
      await client.invalidateQueries({ queryKey: keys.ownApp });
      setDisconnecting(false);
      setForcing(false);
    } catch (err) {
      toastError(err);
    }
  };
  const keepConnection = async () => {
    try {
      await apply(await api.delete<OrgSettings>("/api/connection/disconnect"));
    } catch (err) {
      toastError(err);
    }
  };
  const copy = async () => {
    await navigator.clipboard.writeText(restrictScript(settings));
    toast.success(t("common.copied"));
  };

  const consentButton = (
    <Button variant={connected ? "secondary" : "primary"} size={connected ? "md" : "sm"} asChild>
      <a href="/api/connection/consent">
        <ArrowSquareOutIcon />
        {connected ? t("settings.microsoft.reconnect") : t("settings.microsoft.connect")}
      </a>
    </Button>
  );
  const ownAppButton = (label: string, primary = false) => (
    <Button variant={primary ? "primary" : "secondary"} size={primary ? "sm" : "md"} asChild>
      <Link to="/connect/own-app">
        <KeyIcon />
        {label}
      </Link>
    </Button>
  );

  return (
    <div className="grid gap-6">
      <Card>
        <CardHeader
          title={t("settings.microsoft.title")}
          actions={
            <span className="flex items-center gap-2 text-[13px] font-medium">
              <StatusDot tone={connected ? "success" : status === "error" ? "danger" : "neutral"} />
              {t(`settings.microsoft.status.${status}`)}
            </span>
          }
        />
        <div className="flex flex-col gap-4 p-5">
          {cleanup && (
            <div className="flex flex-col gap-3 rounded-md border border-warning/30 bg-warning-soft px-4 py-3 text-[12.5px] text-warning">
              <div>
                <div className="font-medium">
                  {t("settings.microsoft.cleanup.title", { count: cleanup.mailboxesLeft })}
                </div>
                <p className="mt-0.5">
                  {connected ? t("settings.microsoft.cleanup.text") : t("settings.microsoft.cleanup.blocked")}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={keepConnection}>
                  {t("settings.microsoft.cleanup.keep")}
                </Button>
                {!connected && (
                  <Button size="sm" variant="danger-ghost" onClick={() => setForcing(true)}>
                    {t("settings.microsoft.cleanup.force")}
                  </Button>
                )}
              </div>
            </div>
          )}
          <p className="max-w-2xl text-[13px] text-fg-muted">{t("settings.microsoft.connectText")}</p>
          {settings.connection.error && !connected && (
            <div className="flex items-start gap-2 rounded-md bg-danger-soft px-3 py-2 text-[12.5px] text-danger">
              <WarningIcon className="mt-0.5 size-4 shrink-0" />
              <span className="break-words">{settings.connection.error}</span>
            </div>
          )}

          {connected || (method === "own_app" && ownApp) ? (
            <>
              <div className="flex flex-col gap-1.5 rounded-md border border-border bg-subtle px-4 py-3 text-[12.5px]">
                <div className="flex items-center gap-2 font-medium">
                  {method === "own_app" ? (
                    <KeyIcon className="size-4" />
                  ) : (
                    <ShieldCheckIcon className="size-4" />
                  )}
                  {t(`settings.microsoft.method.${method}`)}
                </div>
                {ownApp && (
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-fg-muted">
                    <span>
                      {t("settings.microsoft.clientId")}{" "}
                      <code className="font-mono text-[11.5px]">{ownApp.clientId}</code>
                    </span>
                    <span>
                      {ownApp.kind === "certificate"
                        ? t("settings.microsoft.certificateUntil", {
                            date: formatDate(ownApp.certificateExpiresAt),
                          })
                        : t("settings.microsoft.secretStored", { hint: ownApp.secretHint ?? "" })}
                    </span>
                  </div>
                )}
              </div>
              {connected && settings.connection.notifications === "users_only" && (
                <p className="max-w-2xl rounded-md bg-subtle px-3 py-2 text-[12.5px] text-fg-muted">
                  {t(`settings.microsoft.groupsPending.${method}`)}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-2">
                {method === "own_app"
                  ? ownAppButton(t("settings.microsoft.editOwnApp"))
                  : settings.connection.consentUrlAvailable && consentButton}
                <Button onClick={test} loading={testing}>
                  <CheckCircleIcon />
                  {t("settings.microsoft.test")}
                </Button>
                {!cleanup && (
                  <Button variant="danger-ghost" onClick={() => setDisconnecting(true)}>
                    {t("settings.microsoft.disconnect")}
                  </Button>
                )}
                {settings.connection.checkedAt && (
                  <span className="text-[12px] text-fg-subtle">
                    {t("settings.microsoft.checked", {
                      time: formatRelative(settings.connection.checkedAt),
                    })}
                  </span>
                )}
              </div>
            </>
          ) : (
            <div className="flex flex-col gap-3 md:flex-row">
              <MethodOption
                icon={<ShieldCheckIcon />}
                title={t("settings.microsoft.method.consent")}
                text={t("settings.microsoft.methodText.consent")}
                badge={t("ownApp.recommended")}
                action={consentButton}
                note={settings.connection.consentUrlAvailable ? null : t("settings.microsoft.notConfigured")}
              />
              <MethodOption
                icon={<KeyIcon />}
                title={t("settings.microsoft.method.own_app")}
                text={t("settings.microsoft.methodText.own_app")}
                action={ownAppButton(t("settings.microsoft.setUpOwnApp"), true)}
                note={settings.connection.ownAppAvailable ? null : t("ownApp.unavailable")}
              />
            </div>
          )}

          <div>
            <div className="mb-2 text-[12.5px] font-medium">{t("settings.microsoft.permissions")}</div>
            <ul className="flex flex-col gap-1.5 text-[12.5px] text-fg-muted">
              {(["contacts", "users", "groups"] as const).map((p) => (
                <li key={p} className="flex items-center gap-2">
                  <ShieldCheckIcon className="size-3.5 text-success" />
                  {t(`settings.microsoft.perms.${p}`)}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Card>
      <Card>
        <CardHeader
          title={t("settings.microsoft.hardenedTitle")}
          description={t("settings.microsoft.hardenedText")}
          actions={
            <Button size="sm" onClick={copy}>
              <CopyIcon />
              {t("common.copy")}
            </Button>
          }
        />
        <pre className="max-h-72 overflow-auto bg-subtle p-5 font-mono text-[11.5px] leading-relaxed text-fg-muted">
          {restrictScript(settings)}
        </pre>
      </Card>
      <Dialog
        open={disconnecting}
        onOpenChange={setDisconnecting}
        title={t("settings.microsoft.disconnectTitle")}
        description={t("settings.microsoft.disconnectText")}
        footer={
          <>
            <Button onClick={() => setDisconnecting(false)}>{t("common.cancel")}</Button>
            <Button variant="danger" onClick={() => disconnect()}>
              {t("settings.microsoft.disconnect")}
            </Button>
          </>
        }
      />
      <Dialog
        open={forcing}
        onOpenChange={setForcing}
        title={t("settings.microsoft.cleanup.forceTitle")}
        description={t("settings.microsoft.cleanup.forceText")}
        footer={
          <>
            <Button onClick={() => setForcing(false)}>{t("common.cancel")}</Button>
            <Button variant="danger" onClick={() => disconnect(true)}>
              {t("settings.microsoft.cleanup.force")}
            </Button>
          </>
        }
      />
    </div>
  );
}

function DeliveryTab({ settings }: { settings: OrgSettings }) {
  const { t } = useTranslation();
  const save = useSaveSettings();
  const [mode, setMode] = useState<DeliveryMode>(settings.deliveryMode);
  const [folderName, setFolderName] = useState(settings.folderName);
  const [noteText, setNoteText] = useState(settings.noteText);
  const [categories, setCategories] = useState(settings.categoriesEnabled);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await save({ deliveryMode: mode, folderName, noteText, categoriesEnabled: categories });
      toast.success(t("settings.delivery.saved"));
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const option = (value: DeliveryMode, icon: React.ReactNode, title: string, text: string) => (
    <button
      type="button"
      onClick={() => setMode(value)}
      className={cn(
        "flex flex-1 gap-3 rounded-lg border p-4 text-left transition-colors",
        mode === value
          ? "border-accent bg-accent-soft/50 ring-3 ring-accent/10"
          : "border-border hover:border-border-strong",
      )}
    >
      <span className={cn("mt-0.5 [&_svg]:size-5", mode === value ? "text-accent" : "text-fg-subtle")}>
        {icon}
      </span>
      <span>
        <span className="block text-[13.5px] font-medium">{title}</span>
        <span className="mt-0.5 block text-[12.5px] text-fg-muted">{text}</span>
      </span>
    </button>
  );

  return (
    <Card>
      <CardHeader title={t("settings.delivery.title")} />
      <div className="flex flex-col gap-5 p-5">
        <div className="flex flex-col gap-3 md:flex-row">
          {option(
            "folder",
            <FolderOpenIcon />,
            t("settings.delivery.folder"),
            t("settings.delivery.folderText"),
          )}
          {option("main", <TrayIcon />, t("settings.delivery.main"), t("settings.delivery.mainText"))}
        </div>
        {mode === "folder" && (
          <Field
            label={t("settings.delivery.folderName")}
            hint={t("settings.delivery.folderNameHint")}
            htmlFor="d-folder"
          >
            <Input
              id="d-folder"
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              className="max-w-sm"
            />
          </Field>
        )}
        <Field
          label={t("settings.delivery.noteText")}
          hint={t("settings.delivery.noteHint")}
          htmlFor="d-note"
        >
          <Textarea id="d-note" value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={2} />
        </Field>
        <Field hint={t("settings.delivery.categoriesHint")}>
          <Switch checked={categories} onCheckedChange={setCategories} className="text-[13px]">
            {t("settings.delivery.categories")}
          </Switch>
        </Field>
        <div>
          <Button variant="primary" onClick={submit} loading={busy}>
            {t("common.save")}
          </Button>
        </div>
      </div>
    </Card>
  );
}

export function SettingsPage() {
  const { t } = useTranslation();
  const params = useParams({ from: "/app/settings/{-$tab}" });
  const navigate = useNavigate();
  const { data: settings } = useSettings();
  const tab = params.tab ?? "general";

  return (
    <Page>
      <PageHeader title={t("settings.title")} />
      <Tabs
        className="mb-6"
        value={tab}
        onValueChange={(v) => navigate({ to: "/settings/{-$tab}", params: { tab: v } })}
        tabs={(
          [
            "general",
            "members",
            "microsoft",
            "delivery",
            "integrations",
            "api",
            "billing",
            "partner",
          ] as const
        ).map((v) => ({
          value: v,
          label: t(`settings.tabs.${v}`),
        }))}
      />
      {!settings ? (
        <Skeleton className="h-48" />
      ) : tab === "members" ? (
        <MembersTab />
      ) : tab === "microsoft" ? (
        <MicrosoftTab settings={settings} />
      ) : tab === "delivery" ? (
        <DeliveryTab settings={settings} />
      ) : tab === "integrations" ? (
        <IntegrationsTab />
      ) : tab === "api" ? (
        <ApiTab />
      ) : tab === "billing" ? (
        <BillingTab />
      ) : tab === "partner" ? (
        <PartnerTab />
      ) : (
        <GeneralTab settings={settings} />
      )}
    </Page>
  );
}
